import {useEffect, useMemo, useState} from 'react'
import type {EstablishedAhpSessionCommands} from '@github-ui/ahp-session-client/contracts'
import {isSessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import {SlashCommandMenu} from '@github-ui/copilot-shared-ui/components/SlashCommandMenu'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import {usePermissionsAutocomplete} from '@github-ui/copilot-shared-ui/hooks/use-permissions-autocomplete'
import {
  useStaffDebugArgumentHint,
  useStaffDebugAutocomplete,
} from '@github-ui/copilot-shared-ui/hooks/use-staff-debug-autocomplete'

import {isCloudSessionStoreEnabled} from '@github-ui/copilot-shared-ui/utils/cloud-session-store-flag'

import {isRemoteCommand} from './slash-commands'

interface SlashCommandAutocompleteProps {
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  /** Slash commands only apply to AHP sessions, so gate the dropdown on that. */
  enabled: boolean
  prompt: string
  onPromptChange: (prompt: string) => void
  sessionCommands?: Pick<EstablishedAhpSessionCommands, 'completions'>
}

interface HostCommand {
  insertText: string
  description: string
}

function hostCommandsFromCompletions(
  items: Awaited<ReturnType<EstablishedAhpSessionCommands['completions']>>,
  includeChronicle: boolean,
) {
  return (
    items
      .filter(item => item.attachment.displayKind === 'command')
      .filter(item => includeChronicle || !item.insertText.toLowerCase().startsWith('/chronicle'))
      // The session already runs on GitHub, so `/remote` and its options have
      // nothing to do here.
      .filter(item => !isRemoteCommand(item.insertText))
      .map(item => ({insertText: item.insertText, description: item.attachment.label}))
  )
}

function mergeSuggestions<T extends {key: string}>(...groups: T[][]): T[] {
  const seen = new Set<string>()
  return groups.flat().filter(suggestion => {
    const key = suggestion.key.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * Shapes the host's flat command list into the two-level menu the composer has
 * always shown: command names first, then a command's options once its name is
 * complete. The host returns each option as its own full `"/name option"`
 * entry, so the grouping is derived here rather than advertised by the host.
 */
function groupHostCommands(commands: HostCommand[], typed: string) {
  // Every command name is prefixed by the empty string, so an emptied composer
  // would otherwise re-open the whole list and ghost its first entry.
  if (!typed.startsWith('/')) return []

  const descriptions = new Map<string, string>()
  const options = new Map<string, Array<{name: string; description: string}>>()

  for (const command of commands) {
    const separator = command.insertText.indexOf(' ')
    if (separator === -1) {
      descriptions.set(command.insertText, command.description)
      continue
    }
    const name = command.insertText.slice(0, separator)
    const option = command.insertText.slice(separator + 1).trim()
    if (!option) continue
    options.set(name, [...(options.get(name) ?? []), {name: option, description: command.description}])
  }

  for (const [name, commandOptions] of options) {
    if (!typed.startsWith(`${name} `)) continue
    return commandOptions
      .map(option => ({key: `${name} ${option.name}`, displayKey: option.name, description: option.description}))
      .filter(option => option.key.startsWith(typed) && option.key !== typed)
  }

  return (
    [...new Set([...descriptions.keys(), ...options.keys()])]
      .filter(name => name.startsWith(typed))
      // A name with options completes to itself plus a space so its options open
      // next; one without is already complete, so offering it again would leave
      // the menu open on the text it just inserted.
      .filter(name => name !== typed || options.has(name))
      .map(name => ({
        key: name,
        description: descriptions.get(name) ?? '',
        ...(options.has(name) ? {appendSpace: true} : {}),
      }))
  )
}

/**
 * Renders host-authorized and client-owned slash-command autocomplete for the
 * chat composer.
 *
 * The families are merged into a single dropdown rather than rendered as one
 * each. Their prefixes only diverge from the second character on, so a bare `/` --
 * exactly what someone types to discover commands -- matches every command in
 * both families. Two dropdowns would leave the user with overlapping listboxes
 * and two keydown listeners on the same textarea, where one ArrowDown moves two
 * selections and one Enter can fire two selections. One list also keeps the
 * combobox ARIA wiring on the textarea coherent.
 *
 * It subscribes to the composer prompt context directly so that only this small
 * component re-renders on each keystroke, keeping the heavy `Composer` tree out
 * of the typing hot path (the outer composer deliberately avoids reading the
 * prompt for the same reason).
 */
export function SlashCommandAutocomplete({
  textAreaRef,
  enabled,
  prompt,
  onPromptChange,
  sessionCommands,
}: SlashCommandAutocompleteProps) {
  // The permission menu marks the mode the session is already in and offers only
  // the modes the relay would accept, so it agrees with the permissions dropdown,
  // which reads and writes the same session state. A legacy host reaching only
  // manual/allow-all is narrowed here rather than rejected after the fact.
  const {approvalMode, selectableApprovalModes, permissionMode} = useOptionalEstablishedAhpSessionViewState()
  const permissionSuggestions = usePermissionsAutocomplete(prompt, {
    selectableModes: selectableApprovalModes,
    // A legacy host publishes no `approvalMode`, so its mode is only readable
    // through the effective permission mode.
    currentMode: approvalMode ?? (isSessionApprovalMode(permissionMode) ? permissionMode : undefined),
  })
  const staffDebugSuggestions = useStaffDebugAutocomplete(prompt)
  const staffDebugArgumentHint = useStaffDebugArgumentHint(prompt)
  const [hostCommandState, setHostCommandState] = useState<{
    source: SlashCommandAutocompleteProps['sessionCommands']
    commands: HostCommand[]
  }>({source: undefined, commands: []})
  const [dismissedSuggestionsPrompt, setDismissedSuggestionsPrompt] = useState<string | null>(null)
  const [dismissedHintPrompt, setDismissedHintPrompt] = useState<string | null>(null)

  // Chronicle commands read the cloud session store, so drop any the host
  // advertises unless that capability is provisioned for the session.
  const includeChronicle = isCloudSessionStoreEnabled()

  useEffect(() => {
    if (!enabled || !sessionCommands) return

    let cancelled = false
    void (async () => {
      try {
        const items = await sessionCommands.completions('/', 1)
        if (cancelled) return
        const commands = hostCommandsFromCompletions(items, includeChronicle)
        if (commands.length > 0) {
          setHostCommandState(current =>
            current.source === sessionCommands && current.commands.length > 0
              ? current
              : {source: sessionCommands, commands},
          )
        }
      } catch {
        // Typed queries can retry if the preload fails.
      }
    })()

    return () => {
      cancelled = true
    }
  }, [enabled, includeChronicle, sessionCommands])

  useEffect(() => {
    const typedCommand = prompt.trimStart()
    if (!sessionCommands || !typedCommand.startsWith('/')) return

    let cancelled = false
    const timeout = setTimeout(async () => {
      try {
        const offset = textAreaRef.current?.selectionStart ?? prompt.length
        const items = await sessionCommands.completions(prompt, offset)
        if (cancelled) return
        const commands = hostCommandsFromCompletions(items, includeChronicle)
        // The host only answers an unbroken token at the start of the message,
        // so it returns nothing once an option is being typed. Its last answer
        // already carries that command's options, so keep it and narrow the
        // list here instead of dropping the menu mid-command.
        if (commands.length > 0) setHostCommandState({source: sessionCommands, commands})
      } catch {
        // Keep whatever the host last authorized.
      }
    }, 150)

    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [includeChronicle, prompt, sessionCommands, textAreaRef])

  const suggestions = useMemo(
    () =>
      mergeSuggestions(
        permissionSuggestions,
        sessionCommands && hostCommandState.source === sessionCommands
          ? groupHostCommands(hostCommandState.commands, prompt.trimStart())
          : [],
        staffDebugSuggestions,
      ),
    [permissionSuggestions, hostCommandState, prompt, sessionCommands, staffDebugSuggestions],
  )

  const handleDismiss = (dismissedPrompt: string) => {
    setDismissedSuggestionsPrompt(dismissedPrompt)
    setDismissedHintPrompt(dismissedPrompt)
  }

  return (
    <SlashCommandMenu
      suggestions={enabled && dismissedSuggestionsPrompt !== prompt ? suggestions : []}
      inlineHint={enabled && dismissedHintPrompt !== prompt ? staffDebugArgumentHint : undefined}
      onSelect={onPromptChange}
      onDismiss={handleDismiss}
      textAreaRef={textAreaRef}
      currentText={prompt}
      label="Slash command suggestions"
      idPrefix="chat-slash-command"
    />
  )
}
